import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ECurrency } from '../../../types/enums'
import { roundFloatTo } from '../../../utils'
import { RangeTwoThumbs, Select, ToggleSwitch } from '../../atoms'
import {
  InfoCircle,
  InputNumberWithButtons,
  InputPassword,
} from '../../molecules'
import {
  SpoilerContainer,
  SpoilerContent,
  SpoilerHeader,
} from '../../molecules/Spoiler/components'
import type { PrivateTableSettingsType } from '../CreatePrivateTable/types'
import { currencyOptions } from './constants'
import {
  containerStyle,
  currencySelector,
  expandableSettingBlock,
  expandableSettingBlockHeader,
  flexRow,
  input,
  inputNumberWithButton,
  settingBlock,
  settingLabel,
  settingRow,
  settingsBlock,
  settingsHeader,
  spoilerContent,
} from './index.css'

export const TableSettingsForm = (props: {
  settings: PrivateTableSettingsType
  mode: 'create' | 'update'
  limits?: {
    maxPlayers?: {
      min: number
    }
  }
  onChange: <GKey extends keyof PrivateTableSettingsType>(
    field: GKey,
    value: PrivateTableSettingsType[GKey],
  ) => void
}) => {
  const { t } = useTranslation()

  const isCryptoCurrency =
    props.settings.currency === ECurrency.USDT ||
    props.settings.currency === ECurrency.TON

  const minStraddle = props.settings.bigBlind * 2
  const maxStraddle = props.settings.bigBlind * 10

  const [isStraddleExpanded, setIsStraddleExpanded] = useState(
    props.settings.straddleMin != null && props.settings.straddleMax != null,
  )
  const [isBombPotExpanded, setIsBombPotExpanded] = useState(
    props.settings.bombPotMin != null && props.settings.bombPotMax != null,
  )

  useEffect(() => {
    const calculatedSmallBlind = roundFloatTo(props.settings.bigBlind / 2, 2)
    if (props.settings.smallBlind !== calculatedSmallBlind) {
      props.onChange('smallBlind', calculatedSmallBlind)
    }

    const clamp = (value: number, min: number, max: number) =>
      Math.max(min, Math.min(value, max))

    if (isStraddleExpanded) {
      const clampedMin = clamp(
        props.settings.straddleMin ?? minStraddle,
        minStraddle,
        maxStraddle,
      )
      const clampedMax = clamp(
        props.settings.straddleMax ?? maxStraddle,
        minStraddle,
        maxStraddle,
      )
      if (
        clampedMin !== props.settings.straddleMin ||
        clampedMax !== props.settings.straddleMax
      ) {
        props.onChange('straddleMin', clampedMin)
        props.onChange('straddleMax', clampedMax)
      }
    }
  }, [props.settings.bigBlind])

  useEffect(() => {
    const calculatedBigBlind = roundFloatTo(props.settings.smallBlind * 2, 2)
    if (props.settings.bigBlind !== calculatedBigBlind) {
      props.onChange('bigBlind', calculatedBigBlind)
    }
  }, [props.settings.smallBlind])

  useEffect(() => {
    if (isCryptoCurrency) {
      if (
        props.settings.rakePercent !== 0 ||
        props.settings.rakeThreshold !== 0
      ) {
        props.onChange('rakePercent', 0)
        props.onChange('rakeThreshold', 0)
      }
    }
  }, [props.settings.currency])

  useEffect(() => {
    if (props.settings.minBuyIn > props.settings.maxBuyIn) {
      props.onChange('maxBuyIn', props.settings.minBuyIn)
    }
  }, [props.settings.minBuyIn, props.settings.maxBuyIn])

  return (
    <div className={containerStyle}>
      <div className={settingsBlock}>
        <h4 className={settingsHeader}>
          {t('createPrivateGame.label.security')}
        </h4>
        <InputPassword
          placeholder={t('createPrivateGame.placeholder.password')}
          className={input}
          value={props.settings.password || ''}
          onChange={(value) => props.onChange('password', value || '')}
        />
      </div>

      <div className={settingsBlock}>
        <h4 className={settingsHeader}>
          {t('createPrivateGame.label.settings')}
        </h4>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.minPlayers.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.minPlayers.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.minPlayers}
            min={2}
            max={9}
            step={1}
            size={2}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('minPlayers', value)}
          />
        </div>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.maxPlayers.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.maxPlayers.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={Math.max(
              props.settings.maxPlayers,
              props.limits?.maxPlayers?.min || 0,
            )}
            min={Math.max(2, props.limits?.maxPlayers?.min || 2)}
            max={9}
            step={1}
            size={2}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('maxPlayers', value)}
          />
        </div>

        {props.mode === 'create' ? (
          <div className={settingBlock}>
            <span className={settingLabel}>
              {t('createPrivateGame.setting.currency.label')}
              <InfoCircle
                tooltip={t('createPrivateGame.setting.currency.tooltip')}
              />
            </span>
            <Select
              value={props.settings.currency}
              options={currencyOptions}
              className={currencySelector}
              onChange={(e) =>
                props.onChange(
                  'currency',
                  e.target.value as unknown as ECurrency,
                )
              }
            />
          </div>
        ) : null}

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.smallBlind.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.smallBlind.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.smallBlind}
            min={0}
            max={100}
            size={props.settings.smallBlind.toString().length + 1}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('smallBlind', value)}
          />
        </div>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.bigBlind.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.bigBlind.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.bigBlind}
            min={0}
            max={100}
            size={props.settings.bigBlind.toString().length + 1}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('bigBlind', value)}
          />
        </div>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.minBuyIn.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.minBuyIn.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.minBuyIn}
            min={0}
            max={props.settings.maxBuyIn}
            size={props.settings.minBuyIn.toString().length + 1}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('minBuyIn', value)}
          />
        </div>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.maxBuyIn.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.maxBuyIn.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.maxBuyIn}
            min={props.settings.minBuyIn}
            max={Number.POSITIVE_INFINITY}
            step={props.settings.bigBlind * 10}
            size={props.settings.maxBuyIn.toString().length + 1}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('maxBuyIn', value)}
          />
        </div>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.turnSeconds.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.turnSeconds.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.turnSeconds}
            min={10}
            max={60}
            step={5}
            size={props.settings.turnSeconds.toString().length + 1}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('turnSeconds', value)}
          >
            {t('createPrivateGame.common.seconds')}
          </InputNumberWithButtons>
        </div>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.dealDelaySeconds.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.dealDelaySeconds.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={props.settings.dealDelaySeconds}
            min={0}
            max={60}
            step={1}
            size={props.settings.dealDelaySeconds.toString().length + 1}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('dealDelaySeconds', value)}
          >
            {t('createPrivateGame.common.seconds')}
          </InputNumberWithButtons>
        </div>

        <SpoilerContainer
          state={isCryptoCurrency ? 'expand' : 'fold'}
          className={expandableSettingBlock}
        >
          <SpoilerHeader>
            {() => (
              <div className={expandableSettingBlockHeader}>
                <span className={settingLabel}>
                  {t('createPrivateGame.setting.rakePercent.label')}
                  <InfoCircle
                    tooltip={t('createPrivateGame.setting.rakePercent.tooltip')}
                  />
                </span>
                <InputNumberWithButtons
                  value={isCryptoCurrency ? 2 : props.settings.rakePercent}
                  min={1}
                  max={10}
                  step={1}
                  size={props.settings.rakePercent.toString().length + 1}
                  disabled={isCryptoCurrency}
                  className={inputNumberWithButton}
                  onChange={(value) => props.onChange('rakePercent', value)}
                >
                  %
                </InputNumberWithButtons>
              </div>
            )}
          </SpoilerHeader>
          <SpoilerContent>
            {t('createPrivateGame.setting.rakePercent.expand')}
          </SpoilerContent>
        </SpoilerContainer>

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.setting.rakeThreshold.label')}
            <InfoCircle
              tooltip={t('createPrivateGame.setting.rakeThreshold.tooltip')}
            />
          </span>
          <InputNumberWithButtons
            value={isCryptoCurrency ? 3 : props.settings.rakeThreshold}
            min={1}
            max={100}
            step={1}
            size={props.settings.rakeThreshold.toString().length + 1}
            disabled={isCryptoCurrency}
            className={inputNumberWithButton}
            onChange={(value) => props.onChange('rakeThreshold', value)}
          >
            {t('createPrivateGame.common.bb')}
          </InputNumberWithButtons>
        </div>
      </div>

      <div className={settingsBlock}>
        <h4 className={settingsHeader}>
          {t('createPrivateGame.label.additionalSettings')}
        </h4>

        {/* <SpoilerContainer
          state={isStraddleExpanded ? 'expand' : 'fold'}
          className={expandableSettingBlock}
        >
          <SpoilerHeader>
            {() => (
              <div className={expandableSettingBlockHeader}>
                <span className={settingLabel}>
                  {t('createPrivateGame.additionalSettings.straddle.label')}
                  <InfoCircle
                    tooltip={t(
                      'createPrivateGame.additionalSettings.straddle.tooltip',
                    )}
                  />
                </span>
                <ToggleSwitch
                  checked={isStraddleExpanded}
                  onChange={(event) => {
                    const checked = event.target.checked
                    setIsStraddleExpanded(checked)
                    if (checked) {
                      props.onChange('straddleMin', minStraddle)
                      props.onChange('straddleMax', maxStraddle)
                    } else {
                      props.onChange('straddleMin', null)
                      props.onChange('straddleMax', null)
                    }
                  }}
                />
              </div>
            )}
          </SpoilerHeader>
          <SpoilerContent className={spoilerContent}>
            <span className={flexRow}>
              {t('createPrivateGame.common.min')}
              {props.settings.straddleMin != null && (
                <RangeTwoThumbs
                  values={[
                    props.settings.straddleMin,
                    props.settings.straddleMax as number,
                  ]}
                  min={minStraddle}
                  max={maxStraddle}
                  onChange={([min, max]) => {
                    props.onChange('straddleMin', min)
                    props.onChange('straddleMax', max)
                  }}
                />
              )}
              {t('createPrivateGame.common.max')}
            </span>
          </SpoilerContent>
        </SpoilerContainer> */}

        <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.additionalSettings.doubleBoard.label')}
            <InfoCircle
              tooltip={t(
                'createPrivateGame.additionalSettings.doubleBoard.tooltip',
              )}
            />
          </span>
          <ToggleSwitch
            checked={props.settings.isDoubleBoardPossible}
            onChange={(event) =>
              props.onChange('isDoubleBoardPossible', event.target.checked)
            }
          />
        </div>

        <SpoilerContainer
          state={isBombPotExpanded ? 'expand' : 'fold'}
          className={expandableSettingBlock}
        >
          <SpoilerHeader>
            {() => (
              <div className={expandableSettingBlockHeader}>
                <span className={settingLabel}>
                  {t('createPrivateGame.additionalSettings.bombPot.label')}
                  <InfoCircle
                    tooltip={t(
                      'createPrivateGame.additionalSettings.bombPot.tooltip',
                    )}
                  />
                </span>
                <ToggleSwitch
                  checked={isBombPotExpanded}
                  onChange={(event) => {
                    const checked = event.target.checked
                    setIsBombPotExpanded(checked)
                    if (checked) {
                      props.onChange('bombPotMin', props.settings.bigBlind * 2)
                      props.onChange('bombPotMax', props.settings.bigBlind * 4)
                      props.onChange('bombPotFrequency', 1)
                      props.onChange('bombPotActivateDoubleBoard', false)
                    } else {
                      props.onChange('bombPotMin', null)
                      props.onChange('bombPotMax', null)
                      props.onChange('bombPotFrequency', null)
                      props.onChange('bombPotActivateDoubleBoard', null)
                    }
                  }}
                />
              </div>
            )}
          </SpoilerHeader>
          <SpoilerContent className={spoilerContent}>
            <div className={settingRow}>
              <span className={settingLabel}>
                {t(
                  'createPrivateGame.additionalSettings.bombPot.expandable.roundCounter.label',
                )}
                <InfoCircle
                  tooltip={t(
                    'createPrivateGame.additionalSettings.bombPot.expandable.roundCounter.tooltip',
                  )}
                />
              </span>
              {props.settings.bombPotFrequency != null ? (
                <InputNumberWithButtons
                  value={props.settings.bombPotFrequency}
                  min={1}
                  max={25}
                  step={1}
                  size={2}
                  className={inputNumberWithButton}
                  onChange={(value) =>
                    props.onChange('bombPotFrequency', value)
                  }
                />
              ) : null}
            </div>

            <span className={flexRow}>
              {t('createPrivateGame.common.min')}
              {props.settings.bombPotMin != null && (
                <RangeTwoThumbs
                  values={[
                    props.settings.bombPotMin,
                    props.settings.bombPotMax as number,
                  ]}
                  min={1}
                  max={props.settings.bigBlind * 10}
                  onChange={([min, max]) => {
                    props.onChange('bombPotMin', min)
                    props.onChange('bombPotMax', max)
                  }}
                />
              )}
              {t('createPrivateGame.common.max')}
            </span>

            <div className={settingRow}>
              <span className={settingLabel}>
                {t(
                  'createPrivateGame.additionalSettings.bombPot.expandable.activateDoubleBoard.label',
                )}
                <InfoCircle
                  tooltip={t(
                    'createPrivateGame.additionalSettings.bombPot.expandable.activateDoubleBoard.tooltip',
                  )}
                />
              </span>
              {props.settings.bombPotActivateDoubleBoard != null && (
                <ToggleSwitch
                  checked={props.settings.bombPotActivateDoubleBoard}
                  onChange={(event) =>
                    props.onChange(
                      'bombPotActivateDoubleBoard',
                      event.target.checked,
                    )
                  }
                />
              )}
            </div>
          </SpoilerContent>
        </SpoilerContainer>

        {/* <div className={settingBlock}>
          <span className={settingLabel}>
            {t('createPrivateGame.additionalSettings.payToSeeBoard.label')}
            <InfoCircle
              tooltip={t(
                'createPrivateGame.additionalSettings.payToSeeBoard.tooltip',
              )}
            />
          </span>
          <ToggleSwitch
            checked={props.settings.payToSeeBoard}
            onChange={(event) =>
              props.onChange('payToSeeBoard', event.target.checked)
            }
          />
        </div> */}
      </div>
    </div>
  )
}
